"use client";

import React from "react";
import { House, Lightbulb, CircleCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const features = [{
  title: "Fast Local Response",
  description: "When storms hit, we're already in your neighborhood. Our local teams prioritize emergency repairs to prevent further damage to your home.",
  icon: House
}, {
  title: "Honest Communication",
  description: "No hidden fees or confusing jargon. We provide clear, detailed estimates and keep you informed at every stage of your exterior project.",
  icon: Lightbulb
}];
export default function LocalTrustSection() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="262945" id="local-trust-section" className="py-16 md:py-24 bg-background overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {}
          <div className={cn("flex flex-col justify-center transition-all duration-700 ease-out", isSectionVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8")}>
            <div className="mb-8">
              <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-primary tracking-tight mb-4">
                Why 800+ Neighbors Trust Us For Their Roofs
              </h2>
              <p className="text-xl md:text-2xl text-secondary font-medium mb-6">
                Your trusted partner for storm restoration and exterior excellence.
              </p>
              <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-relaxed">
                <p>
                  We know Maryland's weather. We understand hail damage, moisture issues, and the specific building codes in your area.
                </p>
                <p>
                  Our reputation is built on being there when you need us most—providing quick local service and expert insurance claim assistance that national companies can't match.
                </p>
                <p>
                  As your neighbors, we take pride in every shingle we lay, ensuring your home is protected by regional expertise and deep knowledge of local standards.
                </p>
                <p>
                  Free local assessments + transparent, neighborly pricing.
                </p>
              </div>
            </div>

            <div className="space-y-8 mb-8">
              {features.map((feature, i) => <div key={i} data-index={i} className="flex items-start gap-4">
                  <div className="shrink-0 mt-1 bg-secondary/10 p-3 rounded-md text-secondary">
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-default text-xl font-semibold text-primary mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="mt-4 p-6 bg-primary/5 rounded-md border border-primary/20">
              <div className="flex items-start gap-3">
                <CircleCheck className="w-6 h-6 text-secondary shrink-0 mt-0.5" />
                <p className="font-default text-lg font-semibold text-primary">
                  Knowledge of local weather conditions means a roof that lasts for decades.
                </p>
              </div>
            </div>
          </div>

          {}
          <div className={cn("relative transition-all duration-700 ease-out delay-200", isSectionVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8")}>
            <div className="relative rounded-md overflow-hidden shadow-xl aspect-[4/5] lg:aspect-square">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/eagle_exteriors_local_roofing.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/eagle_exteriors_local_roofing.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/eagle_exteriors_local_roofing.png-wvc-sizes" alt="Eagle Exteriors team performing quality roof replacement on a local home" className="w-full h-full object-cover" loading="lazy" />
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent pointer-events-none" />
            </div>
            
            {}
            <div className="absolute -z-10 -bottom-6 -right-6 w-full h-full border-2 border-secondary/30 rounded-md" />
          </div>
        </div>
      </div>
    </section>;
}